import React, { useState, useEffect } from 'react';
import { Table, Tag, Input, Select, DatePicker, Button, Space, message } from 'antd';
import { SearchOutlined, FilterOutlined, EyeOutlined } from '@ant-design/icons';
import { getInternalReports } from '../../services/internalReportingService';
import ReportDetailModal from './ReportDetailModal';

const { RangePicker } = DatePicker;

const ReportsTab = () => {
  const [loading, setLoading] = useState(false);
  const [reports, setReports] = useState([]);
  const [selectedReport, setSelectedReport] = useState(null);
  const [detailVisible, setDetailVisible] = useState(false);
  const [filters, setFilters] = useState({
    search: '',
    type: 'all',
    branch: 'all',
    product: 'all',
    dateRange: [null, null],
  });

  const fetchReports = async () => {
    setLoading(true);
    try {
      const res = await getInternalReports(filters);
      if (res?.status) {
        setReports(res.data);
      }
    } catch (err) {
      console.error('Failed to fetch reports:', err);
      message.error('Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReports();
  }, [filters]);

  const columns = [
    {
      title: 'Report Name',
      dataIndex: 'reportName',
      key: 'reportName',
      render: (text) => <span style={{ fontWeight: 500 }}>{text}</span>,
    },
    {
      title: 'Type',
      dataIndex: 'reportType',
      key: 'reportType',
    },
    {
      title: 'Period',
      key: 'period',
      render: (_, record) => `${record.startDate} to ${record.endDate}`,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        let color = 'default';
        if (status === 'Approved') color = 'success';
        if (status === 'Pending Approval') color = 'warning';
        if (status === 'Rejected') color = 'error';
        return <Tag color={color}>{status}</Tag>;
      },
    },
    {
      title: 'Generated At',
      dataIndex: 'generatedAt',
      key: 'generatedAt',
    },
    {
      title: 'Action',
      key: 'action',
      render: (_, record) => (
        <Button
          type="text"
          icon={<EyeOutlined />}
          onClick={() => {
            setSelectedReport(record);
            setDetailVisible(true);
          }}
        >
          View
        </Button>
      ),
    },
  ];

  return (
    <div className="reports-tab">
      <div className="reg-filter-header">
        <Space wrap size="middle">
          <Input
            placeholder="Search reports..."
            prefix={<SearchOutlined />}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            style={{ width: 250 }}
          />
          <Select
            defaultValue="all"
            style={{ width: 150 }}
            onChange={(val) => setFilters({ ...filters, type: val })}
            placeholder="Report Type"
          >
            <Select.Option value="all">All Types</Select.Option>
            <Select.Option value="Financial">Financial</Select.Option>
            <Select.Option value="Operational">Operational</Select.Option>
            <Select.Option value="Performance">Performance</Select.Option>
          </Select>
          <Select
            defaultValue="all"
            style={{ width: 150 }}
            onChange={(val) => setFilters({ ...filters, branch: val })}
            placeholder="Branch"
          >
            <Select.Option value="all">All Branches</Select.Option>
            <Select.Option value="Lagos">Lagos HQ</Select.Option>
            <Select.Option value="Abuja">Abuja</Select.Option>
            <Select.Option value="PH">Port Harcourt</Select.Option>
          </Select>
          <Select
            defaultValue="all"
            style={{ width: 150 }}
            onChange={(val) => setFilters({ ...filters, product: val })}
            placeholder="Product"
          >
            <Select.Option value="all">All Products</Select.Option>
            <Select.Option value="Loan">Loan</Select.Option>
            <Select.Option value="Card">Card</Select.Option>
            <Select.Option value="POS">POS</Select.Option>
          </Select>
          <RangePicker
            onChange={(dates) => setFilters({ ...filters, dateRange: dates })}
          />
          <Button icon={<FilterOutlined />} onClick={fetchReports}>
            Apply Filters
          </Button>
        </Space>
      </div>

      <Table
        loading={loading}
        columns={columns}
        dataSource={reports}
        rowKey="id"
        pagination={{ pageSize: 10 }}
        className="reg-table"
      />

      <ReportDetailModal
        visible={detailVisible}
        onClose={() => setDetailVisible(false)}
        report={selectedReport}
      />
    </div>
  );
};

export default ReportsTab;
